import i18n from '../i18n';
import RIGHTS_KEYS from '../constants/rights-keys';
import type { RoleOption } from '../services/api/onboardingService';

// Allowlist of roles a site admin can assign to department users.
// Mirrors the components/manage-user/RoleSelector.js.
const DEPT_ASSIGNABLE_ROLES = [
    'Radiologist',
    'Radiographer',
    'DeptAdmin',
    'MRI Physicist',
    'MRSO',
];

interface RightsContext {
    orRights: (rights: string[]) => boolean;
}

// Alternative names/descriptions are keyed by app locale codes ('en', 'da',
// 'pt-BR', …), but the browser language detector can report regional codes
// like 'en-US' or 'da-DK' — so match exact first, then the base language,
// then any regional variant of the same base (e.g. 'pt' → 'pt-PT').
function resolveTranslation(
    map: Record<string, string> | undefined
): string | undefined {
    if (!map) return undefined;
    const lang = i18n.language || 'en';
    if (map[lang]) return map[lang];
    const base = lang.split('-')[0];
    if (map[base]) return map[base];
    const variant = Object.keys(map).find((k) => k.split('-')[0] === base);
    return variant ? map[variant] : undefined;
}

export function getActorDisplayName(actor: { role: string; alternativeNames?: Record<string, string> }): string {
    return resolveTranslation(actor.alternativeNames) || actor.role;
}

export function getActorDescription(actor: {
    description?: string;
    alternativeDescriptions?: Record<string, string>;
}): string {
    return resolveTranslation(actor.alternativeDescriptions) || actor.description || '';
}

export function filterAssignableRoles(
    roles: RoleOption[],
    authContext: RightsContext
): RoleOption[] {
    if (authContext.orRights([RIGHTS_KEYS.manageAllUsers])) {
        return roles;
    }
    if (
        authContext.orRights([
            RIGHTS_KEYS.manageEmployees,
            RIGHTS_KEYS.manageTrialUsers,
        ])
    ) {
        return roles.filter((r) => DEPT_ASSIGNABLE_ROLES.includes(r.role));
    }
    return [];
}
